import { Flex, useColorMode } from '@chakra-ui/react'
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import { Security } from '@okta/okta-react'
import * as Sentry from '@sentry/react'
import { jwtDecode } from 'jwt-decode'
import { useRouter } from 'next/router'
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'

import { useSmartPolling } from '~/components/hooks/polling'
import { useLocalStorage, LocalStorageKey } from '~/components/hooks/useLocalStorage'
import { getAccessToken, setAccessToken } from '~/data/accessToken'
import { useFetchMe, UserFragment } from '~/graphql/generated/query.types'
import MfaPage from '~/pages/mfa'
import OktaLogin from '~/pages/okta-login'
import SamlCallbackPage from '~/pages/saml-callback'
import SignupPage from '~/pages/signup'
import SSOPage from '~/pages/sso'
import SSOSamlPage from '~/pages/sso-saml'
import StartPage from '~/pages/start'
import VerifyPage from '~/pages/verify'
import WaitlistPage from '~/pages/waitlist'
import { UserContext } from '~/util/UserContext'

import { PageSpinner } from './PageSpinner'

/**
 * Page component keeps track of the current user, and provides it to downstream components via the
 * UserContext. If there is no user, renders the Login page.
 */
export function AuthWrapper({ children }: PropsWithChildren<unknown>) {
  const router = useRouter()
  const [oktaDomain] = useLocalStorage(LocalStorageKey.OKTA_DOMAIN)
  const [oktaClientId] = useLocalStorage(LocalStorageKey.OKTA_CLIENT_ID)

  const oktaAuth = useMemo(
    () =>
      oktaClientId && oktaDomain
        ? new OktaAuth({
            issuer: `${oktaDomain}/oauth2/default`,
            clientId: oktaClientId,
            redirectUri: `${window.location.origin}/okta-login`,
          })
        : null,
    [oktaDomain, oktaClientId]
  )
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch(`${process.env.NEXT_PUBLIC_BASE_API_URL}/refresh_token`, {
      method: 'POST',
      credentials: 'include',
    })
      .then(async x => {
        const { accessToken } = await x.json()
        setAccessToken(accessToken)
        setLoading(false)
      })
      .catch(e => console.error(e))
  }, [])
  const restoreOriginalUri = useCallback(
    async (_oktaAuth: OktaAuth, _originalUri: string) => {
      await router.push(toRelativeUrl('/okta-login', window.location.origin))
    },
    [router]
  )

  if (loading) {
    return <PageSpinner />
  }

  if (!getAccessToken()) {
    const content = (
      <Flex width="100vw" height="100vh">
        {router.pathname.startsWith('/signup') ? (
          <SignupPage />
        ) : router.pathname.startsWith('/mfa') ? (
          <MfaPage />
        ) : router.pathname.startsWith('/verify') ? (
          <VerifyPage />
        ) : router.pathname.startsWith('/waitlist') ? (
          <WaitlistPage />
        ) : router.pathname.startsWith('/okta-login') ? (
          (() => {
            return <OktaLogin />
          })()
        ) : router.pathname.startsWith('/sso-saml') ? (
          <SSOSamlPage />
        ) : router.pathname.startsWith('/saml-callback') ? (
          <SamlCallbackPage />
        ) : router.pathname.startsWith('/sso') ? (
          <SSOPage />
        ) : (
          <StartPage />
        )}
      </Flex>
    )
    // SAML pages don't need oktaAuth, so render them directly
    if (router.pathname.startsWith('/sso-saml') || router.pathname.startsWith('/saml-callback')) {
      return content
    }

    // if on sso or okta-login and no oktaAuth we should render null, showing a spinner here
    // results in lots of flashing. Just wait until oktaAuth has been initialized first, it's
    // usually only the first paint.
    if (
      (router.pathname.startsWith('/sso') || router.pathname.startsWith('/okta-login')) &&
      !oktaAuth
    ) {
      return null
    }

    return oktaAuth ? (
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        {content}
      </Security>
    ) : (
      content
    )
  }

  if (router.route === '/_error') {
    return <>{children}</>
  }

  return (
    <UserContextWrapper>
      <Flex width="100vw" height="100vh">
        {children}
      </Flex>
    </UserContextWrapper>
  )
}

function UserContextWrapper({ children }: PropsWithChildren<unknown>) {
  const [user] = useState(jwtDecode<UserFragment>(getAccessToken()))
  const { data, startPolling, stopPolling } = useFetchMe()
  useSmartPolling(30000, startPolling, stopPolling)

  const { colorMode, toggleColorMode } = useColorMode()
  useEffect(() => {
    if (!data) {
      return
    }
    const desiredColorModeString = data?.me?.darkMode ? 'dark' : 'light'
    if (desiredColorModeString === colorMode) {
      return
    }
    toggleColorMode()
  }, [data, colorMode, toggleColorMode])

  useEffect(() => {
    if (data?.me) {
      Sentry.setUser(data?.me)
    }
  }, [data?.me])

  const userFragment: UserFragment = data?.me || {
    __typename: 'GQLUser',
    ...(user as any),
  }
  return <UserContext.Provider value={userFragment}>{children}</UserContext.Provider>
}
